import { Button, Dialog, useMediaQuery } from "@material-ui/core"
import { useEffect, useState } from "react"

import { theme } from "../../shared/theme"
import { AthlinksLogo } from "../../components/shared/AthlinksLogo"
import useReigniteMetadata from "../../lib/firebase/useReigniteMetadata"
import { pushGTMEvent } from "../../utils/googleEvents"

import { useStyles } from "./styles"
import { saveLTMobileAppPromoUserResponse, useCheckLTMobileAppPromoLocalStorageResponse } from "../../utils/ltMobileAppPromo"

const DIALOG_OPENING_DELAY = 5_000

const useIsPromoAvailableForCurrentMaster = (masterId: number | null) => {
  const { isFetchingFirebaseConfig, mobileAppPromoConfig } = useReigniteMetadata()

  if (isFetchingFirebaseConfig || !mobileAppPromoConfig) {
    return false
  }

  return !!masterId && mobileAppPromoConfig.whitelistIds.includes(masterId)
}

type LTMobileAppPromoProps = {
  masterId: number | null
}


const LTMobileAppPromo = ({ masterId }: LTMobileAppPromoProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const styles = useStyles()

  const isShowingMobileAppModal = useIsPromoAvailableForCurrentMaster(masterId)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const onClickClose = () => {
    saveLTMobileAppPromoUserResponse('notNow')
    setIsDialogOpen(false)
    pushGTMEvent({
      event: 'promo_modal_option',
      selectedOption: 'not-now',
    })
  }

  const onClickContinue = () => {
    saveLTMobileAppPromoUserResponse('continue')
    setIsDialogOpen(false)
    pushGTMEvent({
      event: 'promo_modal_option',
      selectedOption: 'continue',
    })
  }

  const userResponse = useCheckLTMobileAppPromoLocalStorageResponse()

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (isShowingMobileAppModal && !userResponse) {
      timer = setTimeout(() => {
        setIsDialogOpen(true)
      }, DIALOG_OPENING_DELAY)
    }

    return () => clearTimeout(timer)
  }, [userResponse, isShowingMobileAppModal])

  useEffect(() => {
    if (isDialogOpen) {
      pushGTMEvent({
        event: 'promo_modal_opened',
        modalStatus: 'opened',
      })
    }
  }, [isDialogOpen])

  return (
    <Dialog
      fullScreen={isMobile}
      fullWidth
      maxWidth='md'
      open={isDialogOpen}
      classes={{ paperFullWidth: styles.paperFullWidth }}
    >
      <div className={styles.backgroundImage}>
        <div className={styles.contentContainer}>
          <AthlinksLogo height='49px' fill='#fff' />

          <div className={styles.contentText}>
            <p><span>Good News - It's all in the app!</span></p>

            <p>You can now find, claim, and share race results in the Life Time app!</p>

            <p>Plus, get access to <span>expert-led training and recovery programs</span> to help you prep for your next big race — all in one place.</p>

            <p><span>No membership needed. Just download and go!</span></p>
          </div>

          <div className={styles.buttonsContainer}>
            <Button variant='outlined' onClick={onClickClose}>
              Not Now
            </Button>

            <a
              href='https://my.lifetime.life/account/event-details.html?accountSource=Athlinks&campaignSource=AthlinksIntroModal'
              target='_blank'
              rel='noopener noreferrer'
            >
              <Button variant='contained' onClick={onClickContinue}>
                Continue To Life Time
              </Button>
            </a>
          </div>

          <p className={styles.footerText}>Already have an Athlinks account? Log in to the Life Time app with your same email and password.</p>
        </div>
      </div>
    </Dialog>
  )
}

export { LTMobileAppPromo }